import type { AuthUserRole, Permission } from '../types/user'

/** Полный упорядоченный список известных прав (совпадает с нормализацией и группами доменов). */
export const ALL_PERMISSIONS: readonly Permission[] = [
  'RequestsViewAll',
  'RequestsManageDeleted',
  'RequestsEditAny',
  'RequestsAssign',
  'RequestsBeAssignee',
  'RequestsCancel',
  'RequestsProcessAssigned',
  'RequestsSplitItems',
  'InvoicesCreate',
  'InvoicesDeleteAny',
  'InvoicesSendToPayment',
  'InvoicesRecordPayment',
  'InvoicesRevertPayment',
  'InvoicesRecordReceipt',
  'InvoicesRevertReceipt',
  'FeedbackViewAll',
  'SystemAdmin',
  'NomenclatureManage',
  'WarehouseViewStock',
  'WarehouseForceRefreshStock',
  'WarehouseIssueRequestCreate',
  'WarehouseIssueRequestViewAll',
  'WarehouseIssueRequestProcess',
  'WarehouseTransferCreate',
  'WarehouseTransferViewAll',
  'WarehouseTransferReceive',
  'MetrologyView',
  'MetrologyManage',
  'MetrologyCalibrate',
  'MetrologyIssue',
  'MetrologyReceive'
]

const ALL_PERMISSIONS_SET = new Set<Permission>(ALL_PERMISSIONS)

export function countKnownPermissions(): number {
  return ALL_PERMISSIONS.length
}

const ROLE_PERMISSION_TEMPLATES: Readonly<Record<AuthUserRole, readonly Permission[]>> = {
  Admin: [
    'RequestsViewAll',
    'RequestsManageDeleted',
    'RequestsEditAny',
    'RequestsAssign',
    'RequestsBeAssignee',
    'RequestsCancel',
    'RequestsProcessAssigned',
    'RequestsSplitItems',
    'InvoicesCreate',
    'InvoicesDeleteAny',
    'InvoicesSendToPayment',
    'FeedbackViewAll',
    'SystemAdmin',
    'NomenclatureManage',
    'WarehouseViewStock',
    'WarehouseForceRefreshStock',
    'WarehouseIssueRequestCreate',
    'WarehouseIssueRequestViewAll',
    'WarehouseIssueRequestProcess',
    'WarehouseTransferCreate',
    'WarehouseTransferViewAll',
    'WarehouseTransferReceive',
    'InvoicesRecordPayment',
    'InvoicesRevertPayment',
    'InvoicesRecordReceipt',
    'InvoicesRevertReceipt',
    'MetrologyView',
    'MetrologyManage',
    'MetrologyCalibrate',
    'MetrologyIssue',
    'MetrologyReceive'
  ],
  Head: [
    'RequestsViewAll',
    'RequestsManageDeleted',
    'RequestsEditAny',
    'RequestsAssign',
    'RequestsBeAssignee',
    'RequestsCancel',
    'RequestsProcessAssigned',
    'RequestsSplitItems',
    'InvoicesCreate',
    'InvoicesDeleteAny',
    'InvoicesSendToPayment',
    'FeedbackViewAll',
    'InvoicesRecordPayment',
    'InvoicesRecordReceipt',
    'InvoicesRevertReceipt'
  ],
  ProcurementManager: [
    'RequestsBeAssignee',
    'RequestsProcessAssigned',
    'RequestsSplitItems',
    'InvoicesCreate',
    'InvoicesRecordPayment',
    'InvoicesRecordReceipt'
  ],
  CatalogEditor: [
    'NomenclatureManage'
  ],
  Storekeeper: [
    'RequestsViewAll',
    'WarehouseViewStock',
    'WarehouseForceRefreshStock',
    'WarehouseIssueRequestViewAll',
    'WarehouseIssueRequestProcess',
    'WarehouseTransferViewAll',
    'WarehouseTransferReceive',
    'InvoicesRecordReceipt',
    'InvoicesRevertReceipt'
  ],
  Accountant: [
    'RequestsViewAll',
    'InvoicesRecordPayment',
    'InvoicesRecordReceipt'
  ],
  User: [
    'WarehouseViewStock',
    'WarehouseIssueRequestCreate',
    'WarehouseTransferCreate',
    'InvoicesRecordReceipt'
  ]
}

export function getRolePermissionTemplate(role: AuthUserRole): readonly Permission[] {
  return ROLE_PERMISSION_TEMPLATES[role]
}

export function normalizePermissions(rawPermissions: unknown, fallbackRole: AuthUserRole): Permission[] {
  const values = Array.isArray(rawPermissions) ? rawPermissions : getRolePermissionTemplate(fallbackRole)
  const uniquePermissions = new Set<Permission>()

  for (const value of values) {
    if (typeof value !== 'string') {
      continue
    }
    if (!ALL_PERMISSIONS_SET.has(value as Permission)) {
      continue
    }
    uniquePermissions.add(value as Permission)
  }

  return ALL_PERMISSIONS.filter((permission) => uniquePermissions.has(permission))
}

export function hasPermission(permissions: readonly Permission[], permission: Permission): boolean {
  return permissions.includes(permission)
}
